/**
 * Hook de gestion de la file d'overlays et de la lecture (vidéo, image, anniversaires).
 * Responsabilité unique : queue, déduplication, passage au suivant, lecture média.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import OverlayService from '../../services/OverlayService';
import {
  BIRTHDAY_EXIT_ANIMATION_MS,
  DEFAULT_BIRTHDAY_DURATION_MS,
  DEFAULT_IMAGE_DURATION_MS,
  DELAY_PLAY_NEXT_AFTER_BIRTHDAY_MS,
  DELAY_PLAY_NEXT_AFTER_VIDEO_MS,
  DELAY_PLAY_NEXT_AFTER_IMAGE_MS,
  PROCESSED_OVERLAYS_MAX_SIZE,
  PROCESSED_OVERLAYS_TRIM_TO,
} from './constants';
import { buildOverlayId } from './overlayUtils';

const isDev =
  typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test';

function logDev(...args) {
  if (isDev) console.log(...args);
}

/**
 * Construit l'objet overlay normalisé à partir des données socket.
 * @param {Object} data - Données reçues de overlay.show
 * @returns {Object}
 */
function normalizeOverlayData(data) {
  const overlayId = buildOverlayId(data);
  const base = {
    id: overlayId,
    rewardId: data.rewardId,
    rewardTitle: data.rewardTitle,
    overlayType: data.overlayType,
    filePath: data.filePath,
    duration: data.duration,
    userName: data.userName,
    timestamp: data.timestamp || new Date().toISOString(),
  };
  if (data.overlayType === 'birthday') {
    return {
      ...base,
      birthdays: data.birthdays || [],
      duration: data.duration ?? DEFAULT_BIRTHDAY_DURATION_MS,
    };
  }
  return base;
}

/**
 * Hook : file d'overlays, lecture courante, passage au suivant.
 * @returns {{
 *   currentOverlay: Object | null,
 *   overlayQueue: Array,
 *   isPlaying: boolean,
 *   birthdayExiting: boolean,
 *   videoRef: React.RefObject,
 *   imageRef: React.RefObject,
 *   timeoutRef: React.RefObject,
 *   playNextOverlay: function,
 *   handleOverlayShow: function
 * }}
 */
export function useOverlayPlayback() {
  const [currentOverlay, setCurrentOverlay] = useState(null);
  const [overlayQueue, setOverlayQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [birthdayExiting, setBirthdayExiting] = useState(false);

  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const timeoutRef = useRef(null);
  const isPlayingRef = useRef(false);
  const processedOverlaysRef = useRef(new Set());
  const playNextOverlayCallCountRef = useRef(0);

  const playNextOverlay = useCallback(() => {
    playNextOverlayCallCountRef.current += 1;
    const callId = playNextOverlayCallCountRef.current;
    logDev(
      `[OverlayPlayer] playNextOverlay() appel #${callId} isPlayingRef=${isPlayingRef.current}`
    );

    if (isPlayingRef.current) {
      logDev(
        `[OverlayPlayer] playNextOverlay() #${callId} ignoré (déjà en cours), évite superposition`
      );
      return;
    }

    isPlayingRef.current = true;

    setOverlayQueue((prevQueue) => {
      if (prevQueue.length === 0) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentOverlay(null);
        return prevQueue;
      }

      const [nextOverlay, ...remainingQueue] = prevQueue;
      setCurrentOverlay(nextOverlay);
      setIsPlaying(true);
      return remainingQueue;
    });
  }, []);

  const handleOverlayShow = useCallback(
    (data) => {
      logDev('🎬 Événement overlay.show reçu:', data);

      const overlayId = buildOverlayId(data);

      if (processedOverlaysRef.current.has(overlayId)) {
        if (isDev) console.log('⚠️ Overlay déjà traité, ignoré:', overlayId);
        return;
      }

      processedOverlaysRef.current.add(overlayId);

      if (processedOverlaysRef.current.size > PROCESSED_OVERLAYS_MAX_SIZE) {
        const idsArray = Array.from(processedOverlaysRef.current);
        processedOverlaysRef.current = new Set(
          idsArray.slice(-PROCESSED_OVERLAYS_TRIM_TO)
        );
      }

      const overlayData = normalizeOverlayData(data);

      setOverlayQueue((prevQueue) => {
        const newQueue = [...prevQueue, overlayData];
        if (!isPlayingRef.current && prevQueue.length === 0) {
          setTimeout(() => playNextOverlay(), 0);
        }
        return newQueue;
      });
    },
    [playNextOverlay]
  );

  // Effet : lance la lecture du currentOverlay (vidéo, image ou anniversaires)
  useEffect(() => {
    if (!currentOverlay) return;

    if (currentOverlay.overlayType === 'birthday') {
      const duration = currentOverlay.duration || DEFAULT_BIRTHDAY_DURATION_MS;
      const durationTimeout = setTimeout(() => {
        setBirthdayExiting(true);
        timeoutRef.current = setTimeout(() => {
          isPlayingRef.current = false;
          setIsPlaying(false);
          setCurrentOverlay(null);
          setBirthdayExiting(false);
          timeoutRef.current = null;
          setTimeout(() => playNextOverlay(), DELAY_PLAY_NEXT_AFTER_BIRTHDAY_MS);
        }, BIRTHDAY_EXIT_ANIMATION_MS);
      }, duration);
      timeoutRef.current = durationTimeout;
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }

    if (
      currentOverlay.overlayType !== 'video' &&
      currentOverlay.overlayType !== 'image'
    ) {
      return;
    }

    const fullUrl = OverlayService.getFullUrl(currentOverlay.filePath);

    if (currentOverlay.overlayType === 'video') {
      const videoEl = videoRef.current;
      if (!videoEl) return;

      const onEnded = () => {
        if (isDev) console.log('✅ Vidéo terminée');
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentOverlay(null);
        setTimeout(() => playNextOverlay(), DELAY_PLAY_NEXT_AFTER_VIDEO_MS);
      };

      videoEl.addEventListener('ended', onEnded);
      videoEl.src = fullUrl;
      videoEl.load();
      videoEl.muted = false;
      videoEl
        .play()
        .then(() => {
          if (isDev) console.log('✅ Lecture de la vidéo démarrée');
        })
        .catch((error) => {
          if (isDev) {
            console.warn(
              '⚠️ Autoplay avec son bloqué, tentative en muet:',
              error
            );
          }
          videoEl.muted = true;
          videoEl.play().catch((err) => {
            console.error('❌ Impossible de jouer la vidéo:', err);
            isPlayingRef.current = false;
            setIsPlaying(false);
            setCurrentOverlay(null);
            setTimeout(() => playNextOverlay(), DELAY_PLAY_NEXT_AFTER_VIDEO_MS);
          });
        });

      return () => {
        videoEl.removeEventListener('ended', onEnded);
        videoEl.pause();
        videoEl.removeAttribute('src');
      };
    }

    if (currentOverlay.overlayType === 'image') {
      const imageEl = imageRef.current;
      if (!imageEl) return;

      imageEl.src = fullUrl;
      imageEl.style.opacity = '1';
      if (isDev) console.log("✅ Affichage de l'image démarré");

      const duration = currentOverlay.duration || DEFAULT_IMAGE_DURATION_MS;
      timeoutRef.current = setTimeout(() => {
        if (imageRef.current) {
          imageRef.current.style.opacity = '0';
        }
        isPlayingRef.current = false;
        setIsPlaying(false);
        setCurrentOverlay(null);
        setTimeout(() => playNextOverlay(), DELAY_PLAY_NEXT_AFTER_IMAGE_MS);
      }, duration);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [currentOverlay, playNextOverlay]);

  return {
    currentOverlay,
    overlayQueue,
    isPlaying,
    birthdayExiting,
    setBirthdayExiting,
    setCurrentOverlay,
    setIsPlaying,
    playNextOverlay,
    handleOverlayShow,
    videoRef,
    imageRef,
    timeoutRef,
  };
}
